import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Facebook, CheckCircle, AlertCircle, Zap, Copy, Key, Shield, Building2 } from 'lucide-react';
import api from '../../services/api';
import {
  fetchConnectionStatus, fetchCredentialsStatus, saveMetaCredentials, fetchMetaOAuthUrl,
  disconnectMeta, clearConnectUrl, fetchOAuthUrls, connectWithAccessToken, syncNow,
} from '../../redux/metaAdsSlice';
import alertService from '../../utils/alert';

// Whether each connected Page will actually deliver Instant Form leads into
// the CRM. A Page that isn't subscribed to the leadgen webhook silently keeps
// every lead in Meta's Leads Center.
function LeadDeliveryPanel() {
  const [pages, setPages] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/marketing/meta-ads/pages');
      setPages(data.data?.pages || []);
    } catch {
      setPages([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (pageId) => {
    setBusyId(pageId);
    try {
      await api.post(`/marketing/meta-ads/pages/${pageId}/subscribe-leads`);
    } catch { /* result is re-read below either way */ }
    await load();
    setBusyId(null);
  };

  if (!pages || pages.length === 0) return null;
  return (
    <div className="space-y-2 border-t border-[#D8DDE6] pt-4 mt-4">
      <p className="text-[12px] font-bold text-[#2E2E2E]">Lead delivery to CRM</p>
      {pages.map((p) => (
        <div key={p.id} className="flex items-start justify-between gap-3 text-[12px]">
          <div className="min-w-0">
            <div className="font-semibold text-[#2E2E2E]">{p.name}</div>
            {p.leadgenSubscribed ? (
              <div className="text-[#036B3A]">Receiving leads</div>
            ) : (
              <div className="text-[#8E030F]">Not receiving leads{p.leadgenSubscribeError ? ` — ${p.leadgenSubscribeError}` : ''}</div>
            )}
          </div>
          {!p.leadgenSubscribed && (
            <button onClick={() => retry(p.id)} disabled={busyId === p.id} className="flex-shrink-0 text-[12px] font-semibold text-[#0176D3] hover:underline disabled:opacity-50">
              {busyId === p.id ? 'Retrying…' : 'Retry'}
            </button>
          )}
        </div>
      ))}
      <p className="text-[11px] text-[#5C5C5C]">If a Page keeps failing, reconnect Meta Ads and grant the <code>pages_manage_metadata</code> permission.</p>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#D8DDE6]">
        <Icon size={16} className="text-[#1877F2]" />
        <span className="text-[13px] font-bold text-[#2E2E2E]">{title}</span>
      </div>
      <div className="p-4 sm:p-6 space-y-4">{children}</div>
    </div>
  );
}

function CopyField({ label, value, secret = false }) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const shown = secret && !revealed ? '•'.repeat(Math.min(String(value).length, 24)) : value;
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-semibold text-[#706E6B] block">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[12px] font-mono text-[#0176D3] bg-[#F4F6F9] border border-[#D8DDE6] rounded-[4px] px-3 py-2 break-all">
          {shown}
        </code>
        {secret && (
          <button type="button" onClick={() => setRevealed((r) => !r)} className="flex-shrink-0 px-2.5 py-2 border border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] text-[13px] font-semibold text-[#444444] hover:text-[#0176D3]">
            {revealed ? 'Hide' : 'Show'}
          </button>
        )}
        <button onClick={copy} className="flex-shrink-0 p-2 border border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] text-[#5C5C5C] hover:text-[#0176D3]">
          <Copy size={14} />
        </button>
      </div>
      {copied && <p className="text-[11px] text-[#04844B]">Copied!</p>}
    </div>
  );
}

const INPUT = 'w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]';

function CredentialsForm({ form, setForm, hasCredential, saving, saved, onSave }) {
  // Once a secret is saved, the server never sends it back — so the field
  // would otherwise just look empty after saving, which reads as "did that
  // not work?". Show a masked placeholder standing in for the saved secret
  // instead, with an explicit "Change" action to swap into editing it —
  // avoids ever submitting the mask itself as a new secret.
  const [editingSecret, setEditingSecret] = useState(false);
  const trim = (v) => (typeof v === 'string' ? v.trim() : v);
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">App ID</label>
        <div className="relative">
          <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <input
            className={`${INPUT} pl-9`}
            value={form.appId}
            onChange={(e) => setForm((f) => ({ ...f, appId: e.target.value }))}
            onBlur={(e) => setForm((f) => ({ ...f, appId: trim(e.target.value) }))}
            placeholder="990602627938098"
          />
        </div>
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">App Secret</label>
        {hasCredential && !editingSecret ? (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Shield size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
              <div className={`${INPUT} pl-9 bg-[#F4F6F9] text-[#5C5C5C] tracking-widest select-none`}>••••••••••••••••</div>
            </div>
            <button
              type="button"
              onClick={() => setEditingSecret(true)}
              className="flex-shrink-0 text-[12px] font-semibold text-[#0176D3] hover:underline"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative">
            <Shield size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
            <input
              type="password"
              autoFocus={editingSecret}
              className={`${INPUT} pl-9`}
              value={form.appSecret}
              onChange={(e) => setForm((f) => ({ ...f, appSecret: e.target.value }))}
              onBlur={(e) => setForm((f) => ({ ...f, appSecret: trim(e.target.value) }))}
              placeholder="your app secret"
            />
          </div>
        )}
        {hasCredential && (
          <p className="text-[11px] text-[#5C5C5C] mt-1">
            {editingSecret
              ? "Rotated your App Secret on Meta's dashboard? Enter the new one here."
              : 'A secret is already saved for this app. Click Change only if you need to rotate it.'}
          </p>
        )}
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">Webhook Verify Token</label>
        <div className="flex items-center gap-2">
          <input
            className={INPUT}
            value={form.webhookVerifyToken}
            onChange={(e) => setForm((f) => ({ ...f, webhookVerifyToken: e.target.value }))}
            onBlur={(e) => setForm((f) => ({ ...f, webhookVerifyToken: trim(e.target.value) }))}
            placeholder="at least 16 letters or numbers"
          />
          <button type="button" onClick={() => setForm((f) => ({ ...f, webhookVerifyToken: randomToken() }))} className="flex-shrink-0 px-3 py-2 border border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] text-[13px] font-semibold text-[#444444] hover:text-[#0176D3]">
            Generate
          </button>
        </div>
        <p className="text-[11px] text-[#5C5C5C] mt-1">Use at least 16 letters or numbers — a short or guessable value is rejected. Enter this exact same value in the Webhooks product's Verify Token field for leadgen events.</p>
      </div>
      <button
        onClick={() => { onSave(); setEditingSecret(false); }}
        disabled={saving || !form.appId || !form.webhookVerifyToken}
        className="w-full bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-[4px]"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      {saved && <p className="text-[12px] text-[#04844B] flex items-center gap-1"><CheckCircle size={13} /> Saved</p>}
    </div>
  );
}

function randomToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function MetaAdsSettingsPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((s) => s.auth);
  const { connected, configured, credential, urls, connectUrl, tokenStatus, loading, error } = useSelector((s) => s.metaAds);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [form, setForm] = useState({ appId: '', appSecret: '', webhookVerifyToken: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Once connected, the credentials form is collapsed by default behind
  // "Edit App Keys" — previously there was no way at all to update
  // App ID/Secret/Webhook Token without disconnecting first (e.g. after
  // rotating the App Secret on Meta's own dashboard), which looked to users
  // like "credentials aren't saving" when really there was no form to save
  // into.
  const [editingCredentials, setEditingCredentials] = useState(false);

  const oauthError = searchParams.get('error');
  const justConnected = searchParams.get('connected');

  const load = useCallback(() => dispatch(fetchConnectionStatus()), [dispatch]);

  useEffect(() => {
    load();
    dispatch(fetchOAuthUrls());
    dispatch(fetchCredentialsStatus());
  }, [load, dispatch]);

  // Prefill the form (except the secret, which the server never returns) once
  // credentials load. If nothing's saved yet, seed a random webhook token.
  useEffect(() => {
    if (credential) {
      setForm({ appId: credential.appId || '', appSecret: '', webhookVerifyToken: credential.webhookVerifyToken || '' });
    } else if (!form.webhookVerifyToken) {
      setForm((f) => ({ ...f, webhookVerifyToken: randomToken() }));
    }
  }, [credential]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (justConnected) {
      load();
      setSearchParams({}, { replace: true });
    }
  }, [justConnected, load, setSearchParams]);

  useEffect(() => {
    if (connectUrl) {
      window.location.href = connectUrl;
      dispatch(clearConnectUrl());
    }
  }, [connectUrl, dispatch]);

  const saveCredentials = async () => {
    setSaving(true);
    const result = await dispatch(saveMetaCredentials(form));
    setSaving(false);
    if (!result.error) {
      setSaved(true);
      setEditingCredentials(false);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const connect = async () => {
    setConnecting(true);
    await dispatch(fetchMetaOAuthUrl());
    setConnecting(false);
  };

  const [showTokenConnect, setShowTokenConnect] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenConnecting, setTokenConnecting] = useState(false);

  const connectWithToken = async () => {
    if (!tokenInput.trim()) return;
    setTokenConnecting(true);
    const result = await dispatch(connectWithAccessToken(tokenInput.trim()));
    setTokenConnecting(false);
    if (!result.error) {
      setTokenInput('');
      setShowTokenConnect(false);
      load();
    }
  };

  const disconnect = async () => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect this Meta Ads account? Campaign management and lead sync will stop working until you reconnect.', 'danger', 'Disconnect'))) return;
    setDisconnecting(true);
    await dispatch(disconnectMeta());
    setDisconnecting(false);
    load();
  };

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const runSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    const result = await dispatch(syncNow());
    setSyncing(false);
    if (!result.error) {
      setSyncResult({ ok: true, ...result.payload });
      load();
    } else {
      setSyncResult({ ok: false, message: result.payload });
    }
  };

  const hasCredential = configured && !!credential?.appId;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 bg-[#F3F2F2] border border-[#D8DDE6] rounded-[4px] px-4 py-2.5">
        <Building2 size={14} className="text-[#5C5C5C] flex-shrink-0" />
        <div className="text-[12px] text-[#3E3E3C]">
          You're viewing Meta Ads settings for{' '}
          <span className="font-bold text-[#2E2E2E]">{user?.company || 'this account'}</span>
          {user?._id && <span className="text-[#5C5C5C]"> ({user._id})</span>}.
          {' '}App credentials and connections are saved per account — make sure this is the account you intend to connect before continuing.
        </div>
      </div>
      {oauthError && (
        <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#8E030F]">{decodeURIComponent(oauthError)}</div>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#8E030F]">{error}</div>
        </div>
      )}

      {connected ? (
        <Section title="Connected Meta Ads Account" icon={Building2}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13px] font-bold text-[#2E2E2E]">{tokenStatus?.businessName || 'Connected'}</div>
              <div className="text-[12px] text-[#5C5C5C]">
                {(tokenStatus?.adAccountIds || []).length} ad account{(tokenStatus?.adAccountIds || []).length === 1 ? '' : 's'} available
                {tokenStatus?.expiresAt && ` · Token expires ${new Date(tokenStatus.expiresAt).toLocaleDateString()}`}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={runSyncNow}
                disabled={syncing}
                className="text-[13px] font-semibold text-[#0176D3] hover:text-[#0153A0] disabled:opacity-60"
              >
                {syncing ? 'Syncing…' : 'Sync Now'}
              </button>
              <button
                onClick={() => setEditingCredentials((v) => !v)}
                className="text-[13px] font-semibold text-[#0176D3] hover:text-[#0153A0]"
              >
                {editingCredentials ? 'Cancel' : 'Edit App Keys'}
              </button>
              <button
                onClick={disconnect}
                disabled={disconnecting}
                className="text-[13px] font-semibold text-[#BA0517] hover:text-[#8E030F] disabled:opacity-60"
              >
                {disconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>

          {syncResult && (
            <div className={`mt-3 text-[12px] rounded-[4px] px-3 py-2 ${syncResult.ok ? 'bg-[#E3F3E8] text-[#036B3A]' : 'bg-[#FDE7E9] text-[#8E030F]'}`}>
              {syncResult.ok
                ? `Synced ${syncResult.date} — ${syncResult.synced} ad account${syncResult.synced === 1 ? '' : 's'} updated${syncResult.failed ? `, ${syncResult.failed} failed` : ''}.`
                : `Sync failed: ${syncResult.message}`}
            </div>
          )}

          <LeadDeliveryPanel />

          {urls && (
            <div className="space-y-3 border-t border-[#D8DDE6] pt-4 mt-4">
              <p className="text-[12px] font-bold text-[#2E2E2E]">Meta App Dashboard URLs</p>
              <CopyField label="OAuth Redirect URI" value={urls.callbackUrl} />
              <CopyField label="Webhook Callback URL (Page product → Webhooks, leadgen)" value={urls.webhookUrl} />
              {credential?.webhookVerifyToken && (
                <CopyField label="Webhook Verify Token" value={credential.webhookVerifyToken} secret />
              )}
            </div>
          )}

          {editingCredentials && (
            <div className="border-t border-[#D8DDE6] pt-4 mt-4">
              <p className="text-[12px] font-bold text-[#2E2E2E] mb-3">
                Update your app's credentials — e.g. after rotating the App Secret on Meta's own dashboard.
              </p>
              <CredentialsForm
                form={form}
                setForm={setForm}
                hasCredential={hasCredential}
                saving={saving}
                saved={saved}
                onSave={saveCredentials}
              />
            </div>
          )}
        </Section>
      ) : (
        <Section title="Connect Meta Ads" icon={Zap}>
          <p className="text-[13px] text-[#5C5C5C]">
            This CRM connects to the Marketing API using <strong>your own</strong> Meta Developer App — not a shared app used
            by every customer. If you haven't created one yet, go to{' '}
            <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline font-semibold text-[#0176D3]">
              developers.facebook.com/apps
            </a>{' '}
            and add the <strong>Marketing API</strong> product (requesting <code>ads_management</code>, <code>ads_read</code>,{' '}
            <code>leads_retrieval</code>) plus <strong>Facebook Login</strong>.
          </p>

          {urls && (
            <div className="space-y-3 border-t border-[#D8DDE6] pt-4">
              <p className="text-[12px] font-bold text-[#2E2E2E]">1. Add these to your Meta App Dashboard</p>
              <CopyField label="OAuth Redirect URI" value={urls.callbackUrl} />
              <CopyField label="Webhook Callback URL (Page product → Webhooks, leadgen)" value={urls.webhookUrl} />
            </div>
          )}

          <div className="space-y-3 border-t border-[#D8DDE6] pt-4">
            <p className="text-[12px] font-bold text-[#2E2E2E]">2. Enter your app's credentials</p>
            <CredentialsForm
              form={form}
              setForm={setForm}
              hasCredential={hasCredential}
              saving={saving}
              saved={saved}
              onSave={saveCredentials}
            />
          </div>

          {hasCredential && (
            <div className="border-t border-[#D8DDE6] pt-4">
              <p className="text-[12px] font-bold text-[#2E2E2E] mb-3">3. Connect your ad accounts</p>
              <button
                onClick={connect}
                disabled={connecting || loading}
                className="w-full flex items-center justify-center gap-2 border border-[#D8DDE6] hover:border-[#1877F2] rounded-[4px] px-4 py-3 text-[13px] font-semibold text-[#2E2E2E] transition-colors disabled:opacity-60"
              >
                <Facebook size={16} className="text-[#1877F2]" />
                {connecting ? 'Redirecting…' : 'Connect with Facebook'}
              </button>

              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setShowTokenConnect((v) => !v)}
                  className="text-[12px] font-semibold text-[#0176D3] hover:underline"
                >
                  {showTokenConnect ? 'Hide' : "Redirect not working? Connect with an access token instead"}
                </button>
              </div>

              {showTokenConnect && (
                <div className="mt-3 border-t border-dashed border-[#D8DDE6] pt-3 space-y-2">
                  <p className="text-[11px] text-[#5C5C5C]">
                    Generate a <strong>User Access Token</strong> for your own app at{' '}
                    <a href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noreferrer" className="underline font-semibold text-[#0176D3]">
                      Graph API Explorer
                    </a>{' '}
                    with the scopes <code>ads_management</code>, <code>ads_read</code>, <code>business_management</code>,{' '}
                    <code>pages_show_list</code>, <code>pages_read_engagement</code>, <code>pages_manage_metadata</code>,{' '}
                    <code>pages_manage_ads</code>, <code>leads_retrieval</code>, then paste
                    it below. This bypasses the OAuth redirect entirely — useful while App Domains is still being configured.
                    The token is exchanged server-side for your own long-lived token; nothing is stored beyond that.
                  </p>
                  <input
                    type="password"
                    className={INPUT}
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    onBlur={(e) => setTokenInput(e.target.value.trim())}
                    placeholder="Paste your access token here"
                  />
                  <button
                    onClick={connectWithToken}
                    disabled={tokenConnecting || !tokenInput.trim()}
                    className="w-full bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-[4px]"
                  >
                    {tokenConnecting ? 'Connecting…' : 'Connect with This Token'}
                  </button>
                </div>
              )}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
